import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_56a601c8-b253-4088-9fb6-1f7146328396/artifacts/ve1u5sut_logo%20jotuns%20new1.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Jotuns Basketball Club" className="h-24 mx-auto mb-4" />
          <h1 
            className="text-3xl font-bold text-white"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            PANEL DE ADMINISTRACIÓN
          </h1>
          <p className="text-zinc-400 mt-2">Ingresa tus credenciales para continuar</p>
        </div>

        {/* Form */}
        <form 
          onSubmit={handleSubmit} 
          className="bg-zinc-900/50 border border-white/10 rounded-xl p-8"
          data-testid="login-form"
        >
          {error && (
            <div 
              className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-zinc-300">Correo electrónico</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@jotuns.com"
                  className="pl-10 bg-transparent border-white/20 text-white placeholder:text-zinc-500 focus:border-orange-500 focus:ring-orange-500/20"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-300">Contraseña</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-transparent border-white/20 text-white placeholder:text-zinc-500 focus:border-orange-500 focus:ring-orange-500/20"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3"
              data-testid="login-submit"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </div>
        </form>

        {/* Back link */}
        <div className="text-center mt-6">
          <a 
            href="/" 
            className="text-zinc-400 hover:text-orange-500 transition-colors text-sm"
            data-testid="back-to-home"
          >
            ← Volver al sitio
          </a>
        </div>
      </div>
    </div>
  );
}
